import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { liftangoBrandTokens } from '../../theme/brandTokens';

export type PostcodeAssignmentMapLive = {
  postcode: string;
  originLat: number;
  originLon: number;
  stopLat: number | null;
  stopLon: number | null;
  stopName: string | null;
  /** Distinguishes hub vs OSM marker colour; null when no target was assigned. */
  targetKind: 'hub' | 'osm' | null;
  consolidatedStopLat?: number | null;
  consolidatedStopLon?: number | null;
  consolidatedStopName?: string | null;
  walkingDistanceToConsolidatedKm?: number | null;
  walkingTimeToConsolidatedMin?: number | null;
};

type PostcodeAssignmentMapPanelProps = {
  /** Append-only list of postcode → stop pairs processed so far (same ref the runner updates). */
  trailRef: React.MutableRefObject<PostcodeAssignmentMapLive[]>;
  isLoading?: boolean;
  /** Incremented when consolidation results are recomputed (forces redraw even if last row is unchanged). */
  revision?: number;
};

const UK_DEFAULT: L.LatLngTuple = [54.2, -2.9];
const UK_ZOOM = 6;
const POLL_MS = 280;
/** Explicit height so Leaflet always has drawable pixels inside accordions/parent flex without definite height */
const MAP_VIEWPORT_HEIGHT = { xs: 300, sm: 360, md: 440 } as const;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Live assignment map (postcode origin → hub or OSM transit stop). Polls {@link trailRef} so the runner can push each
 * completed row without React re-renders driving Leaflet (feedback loops). The sidebar legend follows {@link targetKind}
 * in the trail.
 */
function legendSwatch(color: string) {
  return {
    borderRadius: '50%',
    width: 10,
    height: 10,
    flexShrink: 0,
    bgcolor: color,
    boxSizing: 'border-box',
    border: `1px solid ${alpha(color, 0.35)}`,
  } as const;
}

/** Target marker kinds present in the live trail — drives the sidebar legend so hub runs do not imply an OSM swatch (and vice versa). */
export type AssignmentMapLegendTargets = 'hub' | 'osm' | 'both';

function legendTargetsFromTrail(trail: PostcodeAssignmentMapLive[]): AssignmentMapLegendTargets {
  if (!trail.length) return 'both';
  let seenHub = false;
  let seenOsm = false;
  for (let i = 0; i < trail.length; i += 1) {
    const k = trail[i]!.targetKind;
    if (k === 'hub') seenHub = true;
    if (k === 'osm') seenOsm = true;
  }
  if (seenHub && seenOsm) return 'both';
  if (seenHub) return 'hub';
  if (seenOsm) return 'osm';
  /* Rows with assignments still pending or only origins — neutral legend until a stop kind appears. */
  return 'both';
}

const MAP_COLORS = liftangoBrandTokens.postcodeAssignmentMap;

/** Initial geographic bearing from (lat1, lon1) to (lat2, lon2), degrees clockwise from north (0–360). */
function bearingDegrees(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

function midpointLatLng(a: L.LatLng, b: L.LatLng): L.LatLng {
  return L.latLng((a.lat + b.lat) / 2, (a.lng + b.lng) / 2);
}

function haversineDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

function formatKm(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
  return `${value.toFixed(3)} km`;
}

function formatMinutes(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A';
  return `${value.toFixed(1)} min`;
}

function directionArrowDivIcon(color: string, bearingDeg: number, sizePx: number): L.DivIcon {
  const s = Math.round(sizePx);
  const stroke = 'rgba(0,0,0,0.22)';
  /* Tip at top (north); rotated by bearing so it aims origin → destination. */
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24" style="display:block" aria-hidden="true"><path d="M12 3 L21 21 L12 16.5 L3 21 Z" fill="${color}" stroke="${stroke}" stroke-width="1.1" stroke-linejoin="round"/></svg>`;
  const html = `<div style="width:${s}px;height:${s}px;display:flex;align-items:center;justify-content:center;transform:rotate(${bearingDeg}deg);">${svg}</div>`;
  return L.divIcon({
    className: 'postcode-assignment-map-arrow-icon',
    html,
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
}

export const PostcodeAssignmentMapPanel: React.FC<PostcodeAssignmentMapPanelProps> = ({
  trailRef,
  isLoading = false,
  revision = 0,
}) => {
  const { t } = useTranslation('simulation');
  const originLegend = MAP_COLORS.origin;
  const hubLegend = MAP_COLORS.hubTarget;
  const osmLegend = MAP_COLORS.osmStop;
  const consolidatedLegend = '#0f766e';

  const [legendTargets, setLegendTargets] = React.useState<AssignmentMapLegendTargets>('both');
  const lastLegendEmittedRef = useRef<AssignmentMapLegendTargets>('both');

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const overlayRef = useRef<L.LayerGroup | null>(null);
  const lastSerializedRef = useRef<string>('__init__');
  const revisionRef = useRef<number>(revision);

  useEffect(() => {
    revisionRef.current = revision;
  }, [revision]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const map = L.map(el, { zoomControl: true, attributionControl: true }).setView(UK_DEFAULT, UK_ZOOM);
    mapRef.current = map;
    overlayRef.current = L.layerGroup().addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize({ animate: false });
    });
    resizeObserver.observe(el);

    /** Tile layer / panes recover after delayed layout (accordion expand, flex). Safe to call repeatedly. */
    const invalidateLayout = (): void => {
      map.invalidateSize({ animate: false });
    };
    map.whenReady(() => {
      invalidateLayout();
    });

    const syncFromRef = () => {
      const overlay = overlayRef.current;
      const m = mapRef.current;
      if (!overlay || !m) return;

      const trail = trailRef.current;
      const last = trail[trail.length - 1];
      const fingerprint = `${revisionRef.current}|${trail.length}|${
        last
          ? `${last.postcode}|${last.originLat}|${last.originLon}|${last.stopLat ?? ''}|${last.stopLon ?? ''}|${last.targetKind ?? ''}|${last.consolidatedStopLat ?? ''}|${last.consolidatedStopLon ?? ''}|${last.walkingDistanceToConsolidatedKm ?? ''}|${last.walkingTimeToConsolidatedMin ?? ''}`
          : ''
      }`;
      if (fingerprint === lastSerializedRef.current) return;
      lastSerializedRef.current = fingerprint;

      if (!trail.length) {
        overlay.clearLayers();
        m.setView(UK_DEFAULT, UK_ZOOM);
        const emptyLegend = legendTargetsFromTrail(trail);
        if (emptyLegend !== lastLegendEmittedRef.current) {
          lastLegendEmittedRef.current = emptyLegend;
          setLegendTargets(emptyLegend);
        }
        return;
      }

      const originColor = MAP_COLORS.origin;
      const hubColor = MAP_COLORS.hubTarget;
      const osmStopColor = MAP_COLORS.osmStop;
      const consolidatedColor = consolidatedLegend;

      overlay.clearLayers();
      const bounds = L.latLngBounds([]);
      const consolidatedTripCounts = new Map<string, number>();
      for (let i = 0; i < trail.length; i += 1) {
        const live = trail[i]!;
        const hasConsolidated =
          live.consolidatedStopLat != null &&
          live.consolidatedStopLon != null &&
          Number.isFinite(live.consolidatedStopLat) &&
          Number.isFinite(live.consolidatedStopLon);
        if (!hasConsolidated) continue;
        const key = `${live.consolidatedStopLat!.toFixed(6)}|${live.consolidatedStopLon!.toFixed(6)}`;
        consolidatedTripCounts.set(key, (consolidatedTripCounts.get(key) ?? 0) + 1);
      }

      for (const live of trail) {
        const origin = L.latLng(live.originLat, live.originLon);
        bounds.extend(origin);

        const stopOk =
          live.stopLat != null &&
          live.stopLon != null &&
          Number.isFinite(live.stopLat) &&
          Number.isFinite(live.stopLon);
        const stop = stopOk ? L.latLng(live.stopLat!, live.stopLon!) : null;

        if (stopOk && stop != null) {
          bounds.extend(stop);
          const lineColor =
            live.targetKind === 'hub' ? hubColor : live.targetKind === 'osm' ? osmStopColor : osmStopColor;
          L.polyline([origin, stop], {
            color: lineColor,
            weight: 2,
            opacity: 0.72,
            lineCap: 'round',
            lineJoin: 'round',
            dashArray: '4 6',
          })
            .bindPopup(
              `<strong>${escapeHtml(live.postcode)}</strong><br/>Assigned target: ${escapeHtml(
                live.stopName || 'Unknown'
              )}<br/>Crow-fly distance: ${formatKm(haversineDistanceKm(origin.lat, origin.lng, stop.lat, stop.lng))}`
            )
            .addTo(overlay);
        }

        const consolidatedOk =
          live.consolidatedStopLat != null &&
          live.consolidatedStopLon != null &&
          Number.isFinite(live.consolidatedStopLat) &&
          Number.isFinite(live.consolidatedStopLon);
        const consolidatedStop = consolidatedOk
          ? L.latLng(live.consolidatedStopLat!, live.consolidatedStopLon!)
          : null;
        if (consolidatedOk && consolidatedStop != null) {
          bounds.extend(consolidatedStop);
          L.polyline([origin, consolidatedStop], {
            color: consolidatedColor,
            weight: 2.5,
            opacity: 0.8,
            lineCap: 'round',
            lineJoin: 'round',
          })
            .bindPopup(
              `<strong>${escapeHtml(live.postcode)}</strong><br/>Consolidated pickup: ${escapeHtml(
                live.consolidatedStopName || 'Consolidated pickup'
              )}<br/>Walking distance: ${formatKm(live.walkingDistanceToConsolidatedKm)}<br/>Walking time: ${formatMinutes(
                live.walkingTimeToConsolidatedMin
              )}`
            )
            .addTo(overlay);
          const brg = bearingDegrees(origin.lat, origin.lng, consolidatedStop.lat, consolidatedStop.lng);
          const mid = midpointLatLng(origin, consolidatedStop);
          L.marker(mid, {
            icon: directionArrowDivIcon(consolidatedColor, brg, 18),
            interactive: false,
            keyboard: false,
            zIndexOffset: 350,
          }).addTo(overlay);
          L.circleMarker(consolidatedStop, {
            radius: 5,
            color: consolidatedColor,
            weight: 1.25,
            opacity: 0.95,
            fillColor: consolidatedColor,
            fillOpacity: 0.75,
          })
            .bindPopup(
              `<strong>${escapeHtml(live.consolidatedStopName || 'Consolidated pickup')}</strong><br/>Consolidated pickup point<br/>Trips using this point: ${
                consolidatedTripCounts.get(`${consolidatedStop.lat.toFixed(6)}|${consolidatedStop.lng.toFixed(6)}`) ?? 0
              }`
            )
            .addTo(overlay);
        }

        L.circleMarker(origin, {
          radius: 6,
          color: originColor,
          weight: 1.5,
          opacity: 0.95,
          fillColor: originColor,
          fillOpacity: 0.95,
        })
          .bindPopup(
            `<strong>${escapeHtml(live.postcode)}</strong><br/>Postcode origin (geocoded or workbook coordinates)`
          )
          .addTo(overlay);

        if (stopOk && stop != null) {
          const stopColor = live.targetKind === 'hub' ? hubColor : osmStopColor;
          const targetLabel =
            live.targetKind === 'hub'
              ? 'Uploaded hub'
              : live.targetKind === 'osm'
                ? 'OpenStreetMap transit stop'
                : 'Assigned target';
          L.circleMarker(stop, {
            radius: 6,
            color: stopColor,
            weight: 2,
            fillColor: stopColor,
            fillOpacity: 0.75,
          })
            .bindPopup(
              `<strong>${live.stopName ? escapeHtml(live.stopName) : escapeHtml(targetLabel)}</strong><br/>${escapeHtml(targetLabel)}`
            )
            .addTo(overlay);
        }
      }

      if (bounds.isValid()) {
        m.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, animate: false });
      }

      const nextLegend = legendTargetsFromTrail(trail);
      if (nextLegend !== lastLegendEmittedRef.current) {
        lastLegendEmittedRef.current = nextLegend;
        setLegendTargets(nextLegend);
      }
    };

    /** Accordion expand / layout: tiles need invalidateSize once the pane has non-zero size */
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting && e.intersectionRatio > 0);
        if (hit) {
          invalidateLayout();
          syncFromRef();
        }
      },
      { threshold: [0, 0.02, 0.1], rootMargin: '0px' }
    );
    intersectionObserver.observe(el);

    invalidateLayout();
    window.requestAnimationFrame(() => {
      invalidateLayout();
      window.requestAnimationFrame(invalidateLayout);
    });
    const postLayoutTimers = [80, 240, 600].map((ms) =>
      window.setTimeout(() => {
        invalidateLayout();
        syncFromRef();
      }, ms)
    );

    const intervalId = window.setInterval(syncFromRef, POLL_MS);
    syncFromRef();

    return () => {
      window.clearInterval(intervalId);
      postLayoutTimers.forEach((id) => window.clearTimeout(id));
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
      lastSerializedRef.current = '__init__';
      lastLegendEmittedRef.current = 'both';
      setLegendTargets('both');
    };
  }, [trailRef]);

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: 1.5,
        borderColor: t.palette.surface.borderSubtle,
        bgcolor: t.palette.surface.secondary,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      })}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, px: 0.5 }}>
        {t('postcodeAssignment.mapPanel.liveMap')}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: MAP_VIEWPORT_HEIGHT,
          flexShrink: 0,
        }}
      >
        <Paper
          component="aside"
          variant="outlined"
          sx={(t) => ({
            position: 'absolute',
            top: t.spacing(1),
            right: t.spacing(1),
            zIndex: 1100,
            px: 1.25,
            py: 1,
            borderColor: t.palette.surface.borderSubtle,
            bgcolor: alpha(t.palette.surface.elevated, 0.92),
            maxWidth: 200,
          })}
          aria-label={t('postcodeAssignment.mapPanel.legendAria')}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.03, display: 'block', mb: 0.75 }}>
            {t('postcodeAssignment.mapPanel.legend')}
          </Typography>
          <Stack spacing={0.5} sx={{ typography: 'caption', color: 'text.secondary', fontSize: 11 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" component="span" sx={{ lineHeight: 1.35 }}>
              <Box component="span" sx={legendSwatch(originLegend)} />
              <span>{t('postcodeAssignment.mapPanel.postcodeOrigin')}</span>
            </Stack>
            {(legendTargets === 'hub' || legendTargets === 'both') && (
              <Stack direction="row" spacing={0.75} alignItems="center" component="span" sx={{ lineHeight: 1.35 }}>
                <Box component="span" sx={legendSwatch(hubLegend)} />
                <span>{t('postcodeAssignment.mapPanel.hubTarget')}</span>
              </Stack>
            )}
            {(legendTargets === 'osm' || legendTargets === 'both') && (
              <Stack direction="row" spacing={0.75} alignItems="center" component="span" sx={{ lineHeight: 1.35 }}>
                <Box component="span" sx={legendSwatch(osmLegend)} />
                <span>{t('postcodeAssignment.mapPanel.osmTransitStop')}</span>
              </Stack>
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" component="span" sx={{ lineHeight: 1.35 }}>
              <Box
                component="span"
                sx={{
                  width: 12,
                  height: 0,
                  borderTop: `2px solid ${consolidatedLegend}`,
                  flexShrink: 0,
                }}
              />
              <span>{t('postcodeAssignment.mapPanel.solidLine')}</span>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center" component="span" sx={{ lineHeight: 1.35 }}>
              <Box
                component="span"
                sx={{
                  width: 12,
                  height: 0,
                  borderTop: `2px dashed ${hubLegend}`,
                  flexShrink: 0,
                }}
              />
              <span>{t('postcodeAssignment.mapPanel.dottedLine')}</span>
            </Stack>
          </Stack>
        </Paper>
        <Box
          ref={containerRef}
          sx={(t) => ({
            position: 'absolute',
            inset: 0,
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${t.palette.surface.borderSubtle}`,
            '& .leaflet-container': {
              height: '100%',
              width: '100%',
              fontFamily: t.typography.fontFamily,
            },
            '& .postcode-assignment-map-arrow-icon': {
              background: 'transparent !important',
              border: 'none !important',
            },
          })}
          role="application"
          aria-label={t('postcodeAssignment.mapPanel.mapAria')}
        />
        {isLoading && (
          <Box
            sx={(t) => ({
              position: 'absolute',
              inset: 0,
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(t.palette.surface.secondary, 0.52),
              borderRadius: 1,
            })}
            aria-live="polite"
            aria-label={t('postcodeAssignment.mapPanel.updatingMapAria')}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">
                {t('postcodeAssignment.mapPanel.updatingMap')}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
